import formatDateTime from 'utils/formatDateTime';
import { parseDate } from 'utils/utils';
import { isPlaceholder as baseIsPlaceholder } from 'utils/data';

function isReleaseWeek(releaseDateStr) {
	const releaseDate = releaseDateStr?.split('-').length === 3 && parseDate(releaseDateStr);
	if (!releaseDate) return false;
	releaseDate.setHours(0, 0, 0, 0);

	const mondayBeforeRelease = new Date(releaseDate);
	mondayBeforeRelease.setDate(mondayBeforeRelease.getDate() - (mondayBeforeRelease.getDay() + 6) % 7);

	return new Date() >= mondayBeforeRelease;
}

/**
 * Check if a movie is a placeholder
 * @param {Object} movie - Movie object
 * @returns {boolean} True if movie is a placeholder
 */
function isPlaceholder(movie) {
	// CMS preview sends isPlaceholder directly
	if (movie?.placeholder) return true;
	// Static/API data uses published_status
	return baseIsPlaceholder(movie);
}

// TODO - CONSIDER DEFINING DATE FORMAT AS A THUNDR VARIABLE TO ENSURE
export function formatReleaseDate(dateStr, opts) {
	const [year, month, day] = dateStr?.split('-') || [];
	if (!year) return null;

	const hideYear = opts?.compact && +year === new Date().getFullYear();

	if (year && month && day) {
		const date = new Date(year, month - 1, day);
		return formatDateTime(date, hideYear ? 'MMM d' : 'MMM d, yyyy');
	}

	if (year && month) {
		const date = new Date(year, month - 1);
		return formatDateTime(date, hideYear ? 'MMM' : 'MMM, yyyy');
	}

	return year;
}

/** Default CTA text shown on the GLOBAL Hub Landing Page */
// TODO - MAKE ALL THIS TEXT DEFINED IN THUNDR VARIABLES
export function getDefaultLandingCtaText(movie) {
	const status = movie.status;

	// Check for Book Tickets first (only if showtimes page is active AND ticketing date reached)
	const showtimesAvailable = status?.theaters === 'preSale' || status?.theaters === 'inTheaters';
	if (showtimesAvailable) {
		return 'Book tickets';
	}

	// If no showtimes but is in home entertainment, no CTA should be shown
	if (status?.homeEnt === 'homeEnt') {
		return null;
	}

	// Otherwise check if trailers are available
	if (Object.values(movie.trailers || {}).some(trailer => trailer?.url)) {
		return 'Watch trailer';
	}

	return null;
}

// TODO - MAKE ALL THIS TEXT DEFINED IN THUNDR VARIABLES
export function getReleaseMessaging(movie) {
	if (!movie) return null;

	// Check for global override from Movie Info page (affects landing page carousel and movie page as fallback)
	if (movie.releaseMessagingOverride) {
		return movie.releaseMessagingOverride;
	}

	// Auto-generate based on status
	if (isPlaceholder(movie)) return 'In Production';

	const status = movie.status;

	if (status?.theaters === 'inTheaters' && status?.homeEnt === 'homeEnt') {
		return 'In Cinemas and At Home';
	}

	if (status?.theaters === 'inTheaters') {
		return 'In Cinemas Now';
	}

	if (status?.homeEnt === 'homeEnt') {
		return 'Watch At Home';
	}

	if (status?.theaters === 'comingSoon' || status?.theaters === 'preSale') {
		const releaseDateStr = movie.dates?.release;

		if (releaseDateStr) {
			const isWeekOfRelease = isReleaseWeek(releaseDateStr);
			// If it's the week of release, show day of week (e.g. "In Cinemas Thursday"), otherwise show full date (e.g. "In Cinemas Mar 10")
			const dateFormatted = isWeekOfRelease ? formatDateTime(parseDate(releaseDateStr), 'dddd') : formatReleaseDate(releaseDateStr, { compact: true });
			return `In Cinemas ${dateFormatted}`;
		}

		return 'Upcoming';
	}

	return null;
}

// TODO - MAKE ALL THIS TEXT DEFINED IN THUNDR VARIABLES
export function getSimpleStatus(movie) {
	if (!movie?.status) return undefined;
	if (isPlaceholder(movie)) {
		return 'In Production';
	}
	if (movie.status?.theaters === 'inTheaters') {
		return 'In Cinemas';
	}
	if (movie.status?.homeEnt === 'homeEnt') {
		return 'Watch Now';
	}
	if (movie.status?.theaters === 'preSale' || movie.status?.theaters === 'comingSoon') {
		return 'Upcoming';
	}
}

/** Default CTA button text shown on the Movie Page Hero */
export function getDefaultCtaText(movie) {
	// Priority: showtimes > homeEnt page
	// Show "Get Tickets" if in theaters (even if homeEnt is also available)
	// Only show "Watch Now" if homeEnt is available AND no showtimes

	const status = movie.status;

	// Check for Get Tickets first (only if showtimes page is active AND ticketing date reached)
	const showtimesAvailable = status?.theaters === 'preSale' || status?.theaters === 'inTheaters';
	if (showtimesAvailable) {
		return 'Book Tickets';
	}

	// Otherwise check if homeEnt status is active (checks both page active AND date reached)
	if (status?.homeEnt === 'homeEnt') {
		return 'Watch Now';
	}

	return null;
}

export function getDefaultMovieLink(movie) {
	return movie.link || `/films/${movie.slug}/`;
}